import type { CardView } from "@breeze/shared";
import { useNow } from "../hooks/useNow.js";

// Friendly, low-urgency time-ago phrasing. Rounds aggressively so staff
// aren't reading stopwatch output — the exact minute doesn't matter, the
// shape of the gap does.
function formatTimeAgo(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 2) return "about 1 minute ago";
  if (m < 60) return `about ${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 2) return "about 1 hour ago";
  if (h < 24) return `about ${h} hours ago`;
  const d = Math.round(h / 24);
  if (d < 2) return "about 1 day ago";
  if (d < 30) return `about ${d} days ago`;
  const w = Math.round(d / 7);
  if (w < 9) return `about ${w} weeks ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `about ${mo} months ago`;
  const y = Math.round(d / 365);
  return `about ${y} year${y === 1 ? "" : "s"} ago`;
}

export function SLATicker({
  card,
  verbose = false,
}: {
  card: CardView;
  verbose?: boolean;
}): JSX.Element | null {
  useNow(30_000);

  if (!card.lastMessageAt || !card.lastMessageDirection) return null;
  const ageMs = Date.now() - new Date(card.lastMessageAt).getTime();
  const phrase = formatTimeAgo(ageMs);
  const verb = card.lastMessageDirection === "in" ? "received" : "sent";

  return (
    <span
      className="text-[12px] font-medium text-ink-50"
      title={new Date(card.lastMessageAt).toLocaleString()}
    >
      last {verb} {phrase}
      {verbose ? null : null}
    </span>
  );
}
